import React, { Fragment, startTransition, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, size } from 'lodash-es';
import { scroller } from 'react-scroll';
import Collapse from 'reactstrap/lib/Collapse';
import { IncentivesEntities } from 'client/data/models/incentives';
import { ENTER_KEY_CODE, SPACE_KEY_CODE } from 'site-modules/shared/constants/key-codes';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { INCENTIVE_TYPE_CREATIVE_ID } from 'site-modules/shared/constants/incentives';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { getFormattedIncentives } from 'site-modules/shared/utils/incentives';
import { IncentivesListPreview } from 'site-modules/shared/components/incentives/incentives-list-preview/incentives-list-preview';
import { IncentiveInfo } from 'site-modules/incentives/components/incentive-info/incentive-info';

import './incentive-category.scss';

const INCENTIVES_COLLAPSE_NUM = 3;

export function IncentiveCategory({ incentives = [], name, type = '', isMobile = false, isOpen: isOpenProp = false }) {
  const [isOpen, setOpen] = useState(isOpenProp);

  function toggle(event) {
    const scrollTop = get(event, 'target.dataset.scrollTop');
    if (isOpen && scrollTop) {
      scroller.scrollTo(scrollTop, {
        duration: SCROLL_DURATION,
        offset: SCROLL_OFFSET,
        smooth: 'easeInOutQuad',
        isDynamic: true,
      });
    }

    setOpen(!isOpen);
  }

  function onKeyDown(event) {
    if (event.keyCode === ENTER_KEY_CODE || event.keyCode === SPACE_KEY_CODE) {
      event.preventDefault();
      toggle(event);
    }
  }

  useEffect(() => {
    if (isOpen !== isOpenProp) {
      startTransition(() => setOpen(isOpenProp));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenProp]);

  const incentivesNum = size(incentives);
  const displayIncentives = getFormattedIncentives(incentives);
  const expandableProps = incentivesNum
    ? {
        onClick: toggle,
        onKeyDown,
        'aria-expanded': isOpen,
        'aria-controls': type,
        role: 'button',
        tabIndex: 0,
        'data-tracking-id': isOpen ? undefined : 'view_content',
        'data-tracking-value': isOpen ? undefined : 'Show details',
      }
    : {};
  const sectionId = `incentives-${type}`;

  return (
    <ScrollElement
      id={sectionId}
      tag="section"
      className={classnames('card incentive-category', type)}
      data-tracking-parent={INCENTIVE_TYPE_CREATIVE_ID[type]}
    >
      <div {...expandableProps}>
        <div
          className={classnames(
            'd-md-flex align-items-center justify-content-between clearfix p-1 px-md-1_5 pt-md-1_5',
            { 'pb-md-1_5': !incentivesNum }
          )}
        >
          <div className="d-inline-block pe-md-1">
            <h3 className="heading-5 d-inline">{name}</h3>{' '}
            <span
              className={classnames('d-inline-block', {
                'text-primary-darker': incentivesNum,
                'text-gray-dark': !incentivesNum,
              })}
            >
              ({incentivesNum} available)
            </span>
          </div>
          {!!incentivesNum && (
            <div className="toggle-btn border-0 p-0 text-gray-darker float-end">
              {isOpen ? (
                <Fragment>
                  Hide details <span className="text-primary-darker small icon icon-minus3" aria-hidden />
                </Fragment>
              ) : (
                <Fragment>
                  Show details <span className="text-primary-darker small icon icon-plus3" aria-hidden />
                </Fragment>
              )}
            </div>
          )}
        </div>
        {!isOpen && !!incentivesNum && <IncentivesListPreview incentives={displayIncentives} />}
      </div>

      {!!incentivesNum && (
        <Fragment>
          <Collapse
            id={type}
            isOpen={isOpen}
            className={classnames('w-100 px-1 px-md-1_5', {
              'pb-1 pb-md-1_5': displayIncentives.length < INCENTIVES_COLLAPSE_NUM,
            })}
          >
            {displayIncentives.map((incentive, index, arr) => (
              <IncentiveInfo
                key={incentive.id}
                incentive={incentive}
                type={type}
                isLast={index === arr.length - 1}
                isMobile={isMobile}
              />
            ))}
          </Collapse>
          {displayIncentives.length >= INCENTIVES_COLLAPSE_NUM && isOpen && (
            <div
              {...expandableProps}
              className="text-end px-1 px-md-1_5 py-1 py-md-1_5 text-gray-darker"
              data-scroll-top={sectionId}
            >
              Hide details <span className="text-primary-darker small icon icon-minus3" aria-hidden />
            </div>
          )}
        </Fragment>
      )}
    </ScrollElement>
  );
}

IncentiveCategory.propTypes = {
  incentives: IncentivesEntities.SpecificIncentivesGraphql,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  isOpen: PropTypes.bool,
  isMobile: PropTypes.bool,
};
